import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { Id } from "@convex/_generated/dataModel";

import type { BeadState, BeadType } from "../../domain/praxis-beads";

export type RightPanelTab = "spec" | "execution";
export type SpecSubTab = "overview" | "diagram" | "reference";

// Graph camera (pan/zoom). Structurally identical to xyflow's `Viewport`
// but declared locally — the store must not import `@xyflow/react` (the
// React Flow bundle is lazy-loaded per §14.1; a type-only mirror keeps it
// out of the eager chunk graph).
export interface GraphViewport {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 280;

function clampWidth(w: number): number {
  if (typeof window === "undefined") return w;
  const maxPx = Math.max(MIN_WIDTH, window.innerWidth - 320);
  return Math.min(Math.max(w, MIN_WIDTH), maxPx);
}

interface RightPanelState {
  isOpen: boolean;
  activeTab: RightPanelTab;
  // Sub-tab selection persists in the store (not local component
  // useState) so it survives the unmount/remount that happens when a
  // bead detail drawer takes over the panel body (`selectedBeadId !==
  // null` swaps `SpecTab`/`ExecutionTab` out in `TaskPanel`). Local
  // useState was lost on every drawer "back", landing the user on the
  // default sub-tab instead of where they were. (`executionSubTab` was
  // retired in v14 — the Diagram pane moved into SpecTab, leaving
  // ExecutionTab single-pane.)
  specSubTab: SpecSubTab;
  width: number;
  typeFilter: BeadType[];
  collapsedStates: BeadState[];
  selectedBeadId: string | null;
  // Selected factory entry (blueprint node id from the parsed
  // `factoryMarkdown`) for the Blueprint-pane factory detail drawer.
  // Distinct from `selectedBeadId`: unrun blueprint entries have NO
  // Convex bead row, so the detail surface reads the parsed factory
  // definition, not the bead mirror.
  selectedFactoryId: string | null;
  // Pinned execution run for the Execution tab. Stickiness is the
  // whole point — once the user clicks a `#N` chip, in-flight runs
  // do NOT steal the body (spec Q8).
  selectedExecutionId: string | null;
  // Last spec the panel was attached to. Used by `setSpecContext`
  // to clear `selectedExecutionId` only when the spec actually
  // changes (no-op on re-renders with the same id).
  lastSpecId: Id<"specs"> | null;
  // Saved graph camera — ONE camera shared by SpecFactoryGraph and
  // SpecExecutionGraph: both render the same dagre layout of the same
  // factory, so the coordinate spaces are identical and the user's
  // place on the DAG carries across blueprint/run views and the
  // unmount/remount of every drawer round-trip. Cleared only on a real
  // spec change (different topology = meaningless camera) — NOT on
  // panel close or tab switches.
  graphViewport: GraphViewport | null;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setActiveTab: (tab: RightPanelTab) => void;
  setSpecSubTab: (tab: SpecSubTab) => void;
  setWidth: (w: number) => void;
  toggleTypeFilter: (type: BeadType) => void;
  clearTypeFilter: () => void;
  toggleStateCollapsed: (state: BeadState) => void;
  selectBead: (beadId: string | null) => void;
  selectFactory: (factoryId: string | null) => void;
  selectExecution: (id: string | null) => void;
  setSpecContext: (specId: Id<"specs"> | null) => void;
  setGraphViewport: (viewport: GraphViewport | null) => void;
}

const DEFAULT_STATE = {
  // Chat-route default: workbench CLOSED on page load (frontend-doctrine
  // §4.6). The open-by-default from the side-panel era read as a blocking
  // modal once TaskPanel became a centered scrim workbench — entry is now
  // the mini-navbar Workflow button. Mobile coordination
  // (`useMobilePanelCoordination`) still collapses the chat panel while
  // this is true on viewports <lg.
  isOpen: false,
  activeTab: "spec" as RightPanelTab,
  specSubTab: "overview" as SpecSubTab,
  width: DEFAULT_WIDTH,
  typeFilter: [] as BeadType[],
  collapsedStates: [] as BeadState[],
  selectedBeadId: null as string | null,
  selectedFactoryId: null as string | null,
  selectedExecutionId: null as string | null,
  lastSpecId: null as Id<"specs"> | null,
  graphViewport: null as GraphViewport | null,
};

export const useRightPanelStore = create<RightPanelState>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,

      open: () => set({ isOpen: true }),
      close: () =>
        set({
          isOpen: false,
          selectedBeadId: null,
          selectedFactoryId: null,
          selectedExecutionId: null,
        }),
      toggle: () =>
        set((s) => ({
          isOpen: !s.isOpen,
          selectedBeadId: s.isOpen ? null : s.selectedBeadId,
          selectedFactoryId: s.isOpen ? null : s.selectedFactoryId,
        })),
      // Tab clicks double as drawer "back" (selectedBeadId clears) but
      // MUST NOT dismiss the factory drawer — like the sub-tabs below,
      // `selectedFactoryId` is sticky across SPEC↔EXECUTION switches and
      // only the drawer ×, panel close, or a real spec change clears it.
      setActiveTab: (activeTab) => set({ activeTab, selectedBeadId: null }),
      // Sub-tabs are deliberately NOT reset by setActiveTab — each tab
      // keeps its own sub-selection sticky across SPEC↔EXECUTION switches.
      setSpecSubTab: (specSubTab) => set({ specSubTab }),
      setWidth: (w) => set({ width: clampWidth(w) }),
      toggleTypeFilter: (type) =>
        set((s) => ({
          typeFilter: s.typeFilter.includes(type)
            ? s.typeFilter.filter((t) => t !== type)
            : [...s.typeFilter, type],
        })),
      clearTypeFilter: () => set({ typeFilter: [] }),
      toggleStateCollapsed: (state) =>
        set((s) => ({
          collapsedStates: s.collapsedStates.includes(state)
            ? s.collapsedStates.filter((c) => c !== state)
            : [...s.collapsedStates, state],
        })),
      selectBead: (selectedBeadId) => set({ selectedBeadId }),
      selectFactory: (selectedFactoryId) => set({ selectedFactoryId }),
      selectExecution: (selectedExecutionId) => set({ selectedExecutionId }),
      setSpecContext: (specId) =>
        set((s) => {
          // `null` is a loading frame, not a spec change — ExecutionTab
          // remounts after every bead-drawer "back" and its first render
          // passes `specId: null` while `useSpec` hydrates. Treating that
          // as a change wiped `selectedFactoryId` + `selectedExecutionId`
          // on every drawer round-trip. Only a real-id → different-real-id
          // transition clears the per-spec selections.
          if (specId === null || s.lastSpecId === specId) return s;
          return {
            ...s,
            lastSpecId: specId,
            // Factory ids are per-spec — a stale selection against a
            // different spec's blueprint is meaningless. Same for the
            // graph camera: a different blueprint has a different layout.
            selectedFactoryId: null,
            selectedExecutionId: null,
            graphViewport: null,
          };
        }),
      setGraphViewport: (graphViewport) => set({ graphViewport }),
    }),
    {
      name: "clawcraft-right-panel",
      // v15 flips DEFAULT_STATE.isOpen back to false. The v8 open-by-default
      // decision predates the workbench redesign (TaskPanel is now a
      // centered scrim modal, not a slide-out rail) — combined with the
      // reset-to-defaults migrate, every version bump was shoving the
      // "Teach Clawbert a workflow" modal in every user's face on login.
      // Entry is now explicit via the chat mini-navbar Workflow button.
      // (Stamped v15, not v14 — the v14 below had already shipped to prod
      // when this flip merged, and a same-version persist never re-runs
      // migrate; existing users needed one more bump to land closed.)
      // v14 retires `executionSubTab` ("blueprint" | "runs") and widens
      // `specSubTab` with "diagram": the Blueprint/Diagram pane moved
      // from ExecutionTab into SpecTab (Specification · Diagram ·
      // References), leaving ExecutionTab single-pane (run history).
      // Migrate continues to reset everything to defaults per the
      // existing contract, dropping any persisted `executionSubTab`.
      // v13 force-resets persisted activeTab back to "spec" (Workflow) —
      // users who had tapped Execution/Flow were still landing there on
      // load because a same-version persist never re-runs migrate. The
      // workflows pane should always open on the Workflow tab first.
      // v12 adds `graphViewport` (shared graph camera for the factory/
      // execution DAGs — survives the drawer-round-trip remounts the same
      // way the v11 sub-tabs do; cleared only on a real spec change).
      // Migrate continues to reset everything to defaults per the
      // existing contract.
      // v11 lifts the per-tab sub-selections (`specSubTab`,
      // `executionSubTab`) out of local component useState into the
      // store so they survive the unmount/remount when a bead detail
      // drawer takes over the panel body. Pre-v11, clicking a bead then
      // "back" remounted SpecTab/ExecutionTab and reset the sub-tab to
      // its default — you always landed on Overview/Blueprint regardless
      // of where you were. Migrate continues to reset everything to
      // defaults per the existing contract.
      // v10 adds `selectedFactoryId` (Blueprint-pane factory detail
      // drawer selection). Migrate continues to reset everything to
      // defaults per the existing contract.
      // v9 force-resets persisted activeTab back to "spec" default. The
      // execution tab was sticking for users who tapped Execution at any
      // earlier point — the workflows pane should land on Spec by default.
      // Migrate continues to reset everything to defaults per the existing
      // contract (see v6→v7, v7→v8 below).
      // v8 flips DEFAULT_STATE.isOpen false → true so the chat-route task
      // panel is open by page-load default. Migrate drops everything to
      // defaults so existing users with a persisted `isOpen: false` pick
      // up the new open-by-default behavior once.
      // v7 removed the "tasks" activeTab — Tasks moved to its own top-level
      // route (/tasks). Migrate drops everything to default so any persisted
      // `activeTab: "tasks"` falls back to "spec".
      version: 15,
      migrate: () => ({ ...DEFAULT_STATE }),
    },
  ),
);
